import { IAddinDetails } from "../model/Addin";
import { CancelablePromise as Promise } from "./cancelablePromise";
import * as logger from "./logger";

let pageContentExtractor = /<body[^>]*>([\s\S]*)<\/body[\s]*>/im;
let scriptsExtractor = /<script[^>]*>([\s\S]*?)<\/script[\s]*>/igm;
let crossOriginExtractor = /crossorigin(=['"]([^'"]*)['"])?/i;
let titleExtractor = /<title[^>]*>([^<]*)<\/title[\s]*>/igm;
let srcReg = /<script[^>]*src[\s]?=[\s]?['"]\S*['"][^>]*>/i;
let srcParamReg = /src[\s]?=[\s]?['"]\S*['"]/i;
let typeReg = /<script[^>]*type[\s]?=[\s]?['"]\S*['"][^>]*>/i;
let typeParamReg = /type[\s]?=[\s]?['"]\S*['"]/i;
let idReg = /<script[^>]*id[\s]?=[\s]?['"]\S*['"][^>]*>/i;
let idParamReg = /id[\s]?=[\s]?['"]\S*['"]/i;
let searchQuote = /['"]/gi;
let searchLastQuote = /['"]$/gi;
let linksExtractor = /<link[^>]*>/igm;
let doc = document;
let head = doc.getElementsByTagName("HEAD")[0];

let addinsPageFolder = "AddIns/";
let customerPagePrefix = "addin-";

let isAsyncDownloadSupported = "async" in document.createElement("SCRIPT");
let jsType = "text/javascript";
let generateId = () => "id" + Math.random().toString().substr(2);
let checkForNewAddins = function(hash) {
    let newAddinsConstructorName;
    let newName;

    if (geotab.addin && hash) {
        newAddinsConstructorName = Object.keys(geotab.addin).filter(key => typeof geotab.addin[key] === "function" && !geotab.addin[key].hasOwnProperty("hash"))[0];

        if (newAddinsConstructorName) {
            // avoid losing addins constructors with the same name
            newName = `addin_${ generateId() }`;
            geotab.addin[newName] = geotab.addin[newAddinsConstructorName];
            geotab.addin[newName]["hash"] = hash;
            delete geotab.addin[newAddinsConstructorName];
        }
    }
};
function getAddinVirtualFileName(prefix: any) {
    try {
        const url = new URL(prefix);
        const segments = url.pathname.split("/").filter(Boolean);
        const addinName = segments[1];
        const virtualFileName = `addins/${ addinName }.js`;
        return virtualFileName;
    } catch {
        return null;
    }
}
let loadScript = function(javaScript, prefix, _, success, error) {
    let scriptElem;
    let src;
    const virtualFileName = getAddinVirtualFileName(prefix);

    if (!javaScript.src) {
        scriptElem = document.createElement("SCRIPT");
        scriptElem.setAttribute("type", javaScript.type || jsType);
        scriptElem.text = virtualFileName ? `${ javaScript.script }\n//# sourceURL=${ virtualFileName }` : javaScript.script;

        if (javaScript.id) {
            scriptElem.id = javaScript.id;
        }

        head.appendChild(scriptElem);
        setTimeout(function() {
            success();
        }, 1);
    } else {
        scriptElem = document.createElement("SCRIPT");
        src = prefix && javaScript.src.indexOf("\/\/") === -1 ? `${ prefix }${ javaScript.src }` : javaScript.src;
        scriptElem.async = false;
        scriptElem.src = src;
        scriptElem.type = javaScript.type || jsType;
        javaScript.crossOrigin && scriptElem.setAttribute("crossorigin", javaScript.crossOrigin);
        scriptElem.onerror = error;
        scriptElem.onload = success;
        scriptElem.id = "mod-test-id";
        scriptElem.name = "mod-test-name";

        if (javaScript.id) {
            scriptElem.id = javaScript.id;
        }

        head.appendChild(scriptElem);
    }
    return scriptElem;
};
let parallelDownload = function(scripts, prefix, hash, success, error) {
    let counter = scripts.length;
    let successHandler = function() {
        if (this && this !== window) {
            this.onload = null;
            this.onerror = null;
        }
        checkForNewAddins(hash);
        counter--;

        if (!counter) {
            success();
        }
    };
    let errorHandler = function(e) {
        let err = new Error(`Addins script '${ e.target ? e.target.src : e }' could not be loaded`);
        error(err);
    };

    for (let i = 0; i < scripts.length; i++) {
        loadScript(scripts[i], prefix, hash, successHandler, errorHandler);
    }
};
let sequentialDownload = function(jScripts, prefix, hash, success, error) {
    let scripts = jScripts.slice(0);
    let errorHandler = function(e) {
        let err = new Error(`Addins script '${ e.target ? e.target.src : e }' could not be loaded`);
        error(err);
    };
    let successHandler = function() {
        if (this && this !== window) {
            this.onload = null;
            this.onerror = null;
        }
        checkForNewAddins(hash);
        if (!scripts.length) {
            success();
        } else {
            loadScript(scripts.shift(), prefix, hash, successHandler, errorHandler);
        }
    };

    loadScript(scripts.shift(), prefix, hash, successHandler, errorHandler);
};
let loader = isAsyncDownloadSupported ? parallelDownload : sequentialDownload;
let getLoadedLinks = function() {
    return Array.prototype.reduce.call<HTMLCollection, any[], any>(
        document.getElementsByTagName("LINK"),
        function(result, link) {
            result[(link.getAttribute("href") || "").split("?")[0] || ""] = true;
            return result;
        },
        {} as any
    );
};
let collectLinks = function(content, prefix) {
    let link = linksExtractor.exec(content);
    let links: string[] = [];
    let href;
    let newLink;
    let oldLinks = getLoadedLinks();

    while (link) {
        href = link[0].match(/["']\S*(\.css|\.less)/ig);

        if (href) {
            newLink = href[0].replace("\"", "");
            newLink = prefix && newLink.indexOf("\/\/") === -1 ? `${ prefix }${ newLink }` : newLink;

            if (!oldLinks[newLink]) {
                links.push(newLink);
            }
        }

        link = linksExtractor.exec(content);
    }
    return links;
};
let getScriptAttribute = function(reg, script, param) {
    let pos: number;
    let str: string = (script.match(reg) || [""])[0];

    if (str) {
        str = script.match(param)[0];
        pos = str.search(searchQuote) + 1;
        str = str.substring(pos, str.search(searchLastQuote));
    }

    return str;
};
let replaceImageLinks = function (imgContent, prefix) {
    let oldImage;
    let newImage;
    let src;
    let oldSrc;
    let newSrc;
    let imagesExtractor = /<img[^>]*>/igm;
    let content = imgContent;
    let img = imagesExtractor.exec(content);
    let imgSrcReg = /<img[^>]*src[\s]?=[\s]?['"]\S*['"][^>]*>/i;

    while (img) {
        oldImage = img[0];
        src = getScriptAttribute(imgSrcReg, oldImage, srcParamReg);

        if (src) {
            oldSrc = newSrc = src;
            if (newSrc.substr(0, 2) === "./") {
                newSrc = newSrc.replace("./", "");
            }

            if (prefix && newSrc[0] !== "/" && newSrc.indexOf("\/\/") === -1) {
                newSrc = `${ prefix }${ newSrc }`;
            }

            if (oldSrc !== newSrc) {
                newImage = oldImage.replace(oldSrc, newSrc);
                content = content.replace(oldImage, newImage);
            }
        }

        img = imagesExtractor.exec(content);
    }
    return content;
};
let collectAllScripts = function(content) {
    let scripts: any[] = [];
    let script = scriptsExtractor.exec(content);

    while (script) {
        let tag = script[0] || "";
        let crossOrigin = "";
        let crossOriginAttribute;

        let strScript = script[1] || "";

        crossOriginAttribute = crossOriginExtractor.exec(tag);
        if (crossOriginAttribute) {
            crossOrigin = crossOriginAttribute[2] || "anonymous";
        }

        scripts.push({
            type: getScriptAttribute(typeReg, tag, typeParamReg),
            src: getScriptAttribute(srcReg, tag, srcParamReg),
            id: getScriptAttribute(idReg, tag, idParamReg),
            script: strScript,
            crossOrigin: crossOrigin
        });

        script = scriptsExtractor.exec(content);
    }

    return scripts;
};

export function injectScripts(scripts, prefix, hash) {
    return new Promise((resolve, reject) => {
        if (scripts && scripts.length) {
            const inlineScriptCount = scripts?.filter(s => !s.src)?.length;
            logger.log(
                logger.Source.ADDIN,
                `injectScripts called for: ${ hash } (prefix: ${ prefix }, script count: ${ scripts.length }, inline script count: ${ inlineScriptCount })`
            );
            loader(scripts, prefix, hash, resolve, reject);
        } else {
            resolve();
        }
    });
}

export function injectLinks(links) {
    let linkElem;

    links && links.forEach(function(link) {
        linkElem = document.createElement("LINK");
        linkElem.setAttribute("href", link);
        linkElem.setAttribute("rel", "stylesheet");

        head.appendChild(linkElem);
    });
}

export function splitPageOnParts(content, urlPrefix) {
    let bodyContent;
    let title;
    let result = {
        scripts: [] as any[],
        bodyContent: "",
        title: "",
        links: [] as any[],
        error: null
    };

    try {
        pageContentExtractor["lastIndex"] = 0;
        scriptsExtractor["lastIndex"] = 0;
        titleExtractor["lastIndex"] = 0;

        bodyContent = pageContentExtractor.exec(content);

        if (bodyContent) {
            bodyContent = bodyContent[1];
            result.scripts = collectAllScripts(content); //isExternalLink ? content : bodyContent);
            result.bodyContent = bodyContent.replace(scriptsExtractor, "");
            result.bodyContent = replaceImageLinks(result.bodyContent, urlPrefix);
            title = titleExtractor.exec(content);
            result.title = title ? title[1] : "";
            result.links = collectLinks(content, urlPrefix);
        }

    } catch (e) {
        result.error = e;
    }

    return result;
}

export function customPageControlFunc(pageName: string) {
    const allAddins = Object.keys(geotab.addin);

    for (let i = 0; i < allAddins.length; ++i) {
        let customPageName = allAddins[i];
        let constructor = geotab.addin[customPageName];

        if (typeof constructor  === "function" && constructor["hash"] === pageName) {
            return constructor;
        }
    }

    return null;
}


/**
 * Set the "menuName" property for our final Add-in object.
 * @param finalAddinDetails The final Add-in object
 * @param partialAddinDetails The partial representation used to populate the final Add-in object
 * @param language The user's language setting
 */
function setMenuName (finalAddinDetails: IAddinDetails, partialAddinDetails: Partial<IAddinDetails>, language: string) {
    let defaultFallback = "Custom addin";
    if (!partialAddinDetails.menuName) {
        finalAddinDetails.menuName = defaultFallback;
        return;
    }

    // If the Add-in configuration has `menuName` set as a plain string, just use it.
    if (typeof partialAddinDetails.menuName === "string") {
        finalAddinDetails.menuName = partialAddinDetails.menuName;
        return;
    }

    // Otherwise (in the case that `menuName` is set as a lookup dictionary) utilize the user's language setting to
    // try to set the menu name from the dictionary. If there is not a value present for the user's language try to fall
    // back to the English value. Failing that just set it to the default value.
    finalAddinDetails.menuName = partialAddinDetails.menuName[language] ?? partialAddinDetails.menuName["en"] ?? defaultFallback;
}

export function collectCustomAddins(customPages: any[], currentUser) {
    let language = currentUser && currentUser.language ? currentUser.language : "en";
    let addinItemsParser = function(items: any[], supportEmail: string, path: string, onStartup: boolean, onShutdown: boolean, solutionId: string | undefined) {
        let isTheSameDomain = link => link.indexOf("\/\/") === -1;
        let addPrefix = function(link, prefix) {
            let linkWithPrefix = link;

            if (linkWithPrefix && isTheSameDomain(linkWithPrefix)) {
                linkWithPrefix = `${ prefix }${ (linkWithPrefix[0] === "\/" ? linkWithPrefix.substring(1) : linkWithPrefix) }`;
            }

            return linkWithPrefix;
        };
        let getMainFileFolder = function(link, pathToFile) {
            let linkFolders = (link || "").split("\/");
            linkFolders.pop();
            let firstPart = linkFolders.join("\/");

            return `${ isTheSameDomain(link) ? pathToFile : "" }${ firstPart ? `${ firstPart }\/` : "" }`;
        };
        let parseExternalLink = function(link: string, prefix: string) {
            if (isTheSameDomain(link)) {
                let subLink = link;
                if (link.substr(0, 2) === "./") {
                    subLink = link.substr(2);
                }

                let lastPointPos = subLink.lastIndexOf(".");
                let ext = subLink.substr(lastPointPos + 1);
                return (prefix + (subLink.substr(0, lastPointPos))).replace(/\//g, ".") + (ext !== "js" ? `[${ ext }]` : "");
            }

            return link;
        };

        let getRawLink = function(link: string, prefix: string) {
            if (isTheSameDomain(link)) {
                let subLink = link;
                if (link.substr(0, 2) === "./") {
                    subLink = link.substr(2);
                }

                return prefix + subLink;
            }

            return link;
        };

        return (items || []).map(function(addinItem: { url?: string; menuId?: string; icon?: string; path?: string; click?: string; page: string }) {
            let hashStart: number;
            let hashEnd: number;
            let hash;
            let addinsHashPart: string;
            let urlForHash: string;
            let addinObj = {
                supportEmail: supportEmail,
                addinPath: path,
                icon: addinItem.icon ? addPrefix(addinItem.icon, path) : null,
                url: "",
                rawUrl: "",
                mainFileFolder: "",
                menuName: "",
                hash: "",
                path: "",
                solutionId,
                onStartup: onStartup,
                onShutdown: onShutdown
            };

            if (addinItem.url) {
                if (addinItem.url) {
                    addinObj.rawUrl = getRawLink(addinItem.url, path);
                    addinObj.url = parseExternalLink(addinItem.url, path);
                    addinObj.mainFileFolder = getMainFileFolder(addinItem.url, path);

                    if (addinItem.icon) {
                        addinObj.icon = addPrefix(addinItem.icon, addinObj.mainFileFolder.indexOf("\/\/") === -1 ? (`/${ addinObj.mainFileFolder }`) : addinObj.mainFileFolder);
                    }

                    hashStart = addinItem.url.lastIndexOf("/");
                    hashEnd = addinItem.url.lastIndexOf(".");

                    if (hashStart >= hashEnd) {
                        urlForHash = addinItem.url;

                        if (hashStart === urlForHash.length - 1) {
                            urlForHash = urlForHash.substring(0, urlForHash.length - 1);
                            hashStart = urlForHash.lastIndexOf("/");
                        }

                        addinsHashPart = urlForHash.substring(hashStart + 1, urlForHash.length);
                    } else {
                        addinsHashPart = addinItem.url.substring(hashStart + 1, hashEnd);
                    }

                    hash = `${ customerPagePrefix }${ addinsHashPart.replace(/\./g, "_") }`;
                }

                addinObj.hash = hash;
                setMenuName(addinObj, addinItem, language);
                addinObj.path = addinItem.path || "";
            }
            return addinObj;
        });
    };

    return (customPages || [])
        .reduce(function(itemsAcc, addinJSON) {
            try {
                let path = `${ addinsPageFolder }${ addinJSON.name.replace(/\s/ig, "") }/`;
                return [
                    ...itemsAcc,
                    ...addinItemsParser(addinJSON.items, addinJSON.supportEmail, path, addinJSON.onStartup, addinJSON.onShutdown, addinJSON.solutionId)
                ];
            } catch (error) {
                return [
                    ...itemsAcc,
                    { error, addinName: addinJSON.name }
                ];
            }
        }, []);
}

export const functionExportsForTesting = {
    setMenuName
};